import {
  FileSearch,
  Focus,
  NotepadText,
  Settings,
  User,
  MessageSquare
} from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut
} from "@/components/ui/command";
import { useEffect, useRef } from "react";
import { useAppDispatch } from "@/store/useApp";
import { useGenerateReport } from "@/hooks/use-generate-report";
import { useNavigate, useParams } from "react-router";
import { useAppState } from "@/store/useApp";

export const CommandMenu = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { generateReport, isGeneratingReport, isEnabled } = useGenerateReport();
  const isVisible = useAppState((state) => state.commandMenuVisible);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      // Focus the input when menu opens
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        dispatch({ type: "TOGGLE_COMMAND_MENU" });
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, dispatch]);

  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    if (isVisible) {
      dispatch({ type: "TOGGLE_COMMAND_MENU" });
    }
  }

  return (
    <Command
      ref={ref}
      autoFocus
      className="rounded-lg border shadow-md w-[400px] h-auto fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50"
    >
      <CommandInput 
        ref={inputRef}
        placeholder="Type a command or search..." 
        // autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            dispatch({ type: "TOGGLE_COMMAND_MENU" });
          }
        }}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Actions">
          <CommandItem onSelect={() => {
            dispatch({ type: "ENTER_FOCUS_MODE" });
            handleClose()
          }}>
            <Focus className="mr-2 h-4 w-4" />
            <span>Toggle focus mode</span>
            <CommandShortcut>⌘/Ctrl + Shift + F</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => {
            if (documentId) {
              navigate(`/documents/${documentId}/pdf`);
            }
            handleClose()
          }}>
            <FileSearch className="mr-2 h-4 w-4" />
            <span>Show source PDF</span>
          </CommandItem>
          <CommandItem onSelect={() => {
            dispatch({ type: "TOGGLE_SCRATCHPAD" });
            handleClose()
          }}>
            <NotepadText className="mr-2 h-4 w-4" />
            <span>Show scratchpad</span>
          </CommandItem>
          <CommandItem
            disabled={!isEnabled || isGeneratingReport}
            onSelect={() => {
              if (documentId && !isGeneratingReport) {
                void generateReport(documentId);
              }
              handleClose()
            }}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            <span>Generate report</span>
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Preferences">
          <CommandItem onSelect={() => {
            dispatch({ type: "TOGGLE_KEYBOARD_SHORTCUTS" });
            handleClose()
          }}>
            <User className="mr-2 h-4 w-4" />
            <span>View keyboard shortcuts</span>
            <CommandShortcut>⌘/</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => {
            window.open('https://docs.clairelabs.ai', '_blank');
            handleClose()
          }}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Open help center</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
